import { PdfGen } from '../../pdf/pdf';
import { ILayoutListGen } from '../layoutList';
import { OptsList, getListItems } from '../optsList';
import { GUtil } from '../../pdf/gutil';
import { GLabel, GTuple } from '../../pdf/glabel';
import {
  ELogicoVariant,
  ELogicoMode,
  drawLogico,
  genMap,
  logicoCount,
} from '../../pdf/board/logico';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import { IItemCount } from '@educraft/base';

abstract class LogicoListBase implements ILayoutListGen, IItemCount {
  constructor(
    protected readonly variant: ELogicoVariant,
    protected readonly mode: ELogicoMode
  ) {}

  countDefault(): number {
    return logicoCount(this.variant);
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const itemsPerPage = logicoCount(this.variant);
    const items = getListItems(opts);
    if (items.length === 0) return;

    for (let i = 0; i < items.length; i += itemsPerPage) {
      const currentPageItems = items.slice(i, i + itemsPerPage);
      const count = currentPageItems.length;

      const page1 = pdf.addPage(); // Question page
      const page2 = pdf.addPage(); // Answer page

      const map = genMap(count, opts.data.random);
      const invMap: number[] = new Array(count);
      for (let j = 0; j < count; j++) invMap[map[j]] = j;

      // labels[j].left  = question shown at visual position j (item value)
      // labels[j].right = correct answer for visual slot j on the answer page (back side)
      // On page 1 (front), visual slot 'j' shows the question for item 'i = invMap[j]'.
      // On page 2 (back), answer column slot 'j' shows the key for item 'j' (always ordered 1-N).
      const labels: GTuple[] = Array.from({ length: count }, (_, j) => ({
        left: GLabel.fromText(currentPageItems[invMap[j]].value),
        right: GLabel.fromText(currentPageItems[j].key),
      }));

      drawLogico(pdf, this.variant, this.mode, labels, map, {
        pageIndex: pdf.gs.indexOf(page1),
        data: opts.data,
      });
    }
  }
}

// ─── Piccolo (10 questions) ───────────────────────────────────────────────────

/** Piccolo__Base — single-column rows */
export class Piccolo__Base extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Rows);
  }
}

/** Piccolo__23 — 2-3-2-3 pattern */
export class Piccolo__23 extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Mode23);
  }
}

/** Piccolo__2C — two-column grid */
export class Piccolo__2C extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.TwoColumns);
  }
}

/** Piccolo__343 — 3-4-3 pattern */
export class Piccolo__343 extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Mode343);
  }
}

/** Piccolo__Okolo — around/border pattern */
export class Piccolo__Okolo extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Around);
  }
}

/** Piccolo__P0 — path pattern 0 */
export class Piccolo__P0 extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Path0);
  }
}

/** Piccolo__P1 — path pattern 1 */
export class Piccolo__P1 extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Path1);
  }
}

/** Piccolo__Pyramid — pyramid pattern */
export class Piccolo__Pyramid extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Pyramid);
  }
}

/** Piccolo__RND — random pattern */
export class Piccolo__RND extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Random);
  }
}

/** Piccolo__Diagonal — diagonal pattern */
export class Piccolo__Diagonal extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Diagonal);
  }
}

// ─── Primo (6 questions) ─────────────────────────────────────────────────────

/** Primo__Base — single-column rows */
export class Primo__Base extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Rows);
  }
}

/** Primo__2C — two-column grid */
export class Primo__2C extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.TwoColumns);
  }
}

/** Primo__P0 — path pattern 0 */
export class Primo__P0 extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Path0);
  }
}

/** Primo__P1 — path pattern 1 */
export class Primo__P1 extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Path1);
  }
}

/** Primo__RND — random pattern */
export class Primo__RND extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Random);
  }
}

/** Primo__Diagonal — diagonal pattern */
export class Primo__Diagonal extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Diagonal);
  }
}

// ─── Maximo (18 questions) ───────────────────────────────────────────────────

/** Maximo__Base — single-column rows */
export class Maximo__Base extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Rows);
  }
}

/** Maximo__2C — two-column grid */
export class Maximo__2C extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.TwoColumns);
  }
}

/** Maximo__RND — random pattern */
export class Maximo__RND extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Random);
  }
}

/** Maximo__Diagonal — diagonal pattern */
export class Maximo__Diagonal extends LogicoListBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Diagonal);
  }
}
